import { AUTH_URL } from 'src/configs';
import request from 'src/request';

import { ListMeta } from './adminUsers';

export type CaseStatus = 'OPEN' | 'RESOLVED';
export type CaseOutcome = 'CLEARED' | 'CONFIRMED' | 'ESCALATED';
export type CaseRiskLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'PROHIBITED';

export type CaseUser = {
  id: string;
  name?: { first?: string; last?: string } | null;
  email?: string | null;
  bxlend_id?: string | null;
  status?: string | null;
  kyc_status?: string | null;
};

export type CaseTransaction = {
  id: string;
  type?: string;
  status?: string;
  currency_code?: string;
  amount?: string;
  chain?: string | null;
  address?: string | null;
  created_at?: string;
};

export type ComplianceCaseListItem = {
  id: string;
  case_number: number;
  status: CaseStatus | string;
  reason?: string;
  reason_detail?: string | null;
  risk_level?: CaseRiskLevel | string;
  outcome?: CaseOutcome | string | null;
  opened_at?: string;
  resolved_at?: string | null;
  open_for_ms?: number;
  assigned_to?: string | null;
  user?: CaseUser | null;
  transaction?: CaseTransaction | null;
};

export type CaseActivity = {
  event?: string;
  message?: string;
  actor?: string | null;
  occurred_at?: string;
};

export type TransactionComplianceDetail = {
  transaction: {
    id: string;
    type?: string;
    status?: string;
    currency_code?: string;
    amount?: string;
    chain?: string | null;
    blockchain?: string | null;
    address?: string | null;
    transaction_hash?: string | null;
    vaultody_reference?: string | null;
    created_at?: string;
    updated_at?: string;
    held_since?: string;
    held_for_ms?: number;
  };
  user?: CaseUser | null;
  screening?: {
    risk_level?: string;
    provider_risk?: string | null;
    reason?: string | null;
    screened_at?: string;
    status?: string;
    recognised?: boolean;
    cluster?: { name?: string | null; category?: string | null };
    address_type?: string | null;
    triggers?: unknown[];
    direct_exposures?: Array<{ category?: string; value?: number; direction?: string }>;
  } | null;
  address_whitelisted?: boolean;
  compliance_case?: {
    id: string;
    case_number: number;
    status?: string;
    reason?: string;
    reason_detail?: string | null;
    risk_level?: string;
    outcome?: CaseOutcome | string | null;
    opened_at?: string;
    resolved_at?: string | null;
  } | null;
  activity?: CaseActivity[];
};

export type CaseDetailResponse = {
  compliance_case: ComplianceCaseListItem & {
    resolution_notes?: string | null;
    assigned_to?: { id: string; name?: { first?: string; last?: string } | null; email?: string | null } | null;
    resolved_by?: { id: string; name?: { first?: string; last?: string } | null; email?: string | null } | null;
  };
  available_outcomes: CaseOutcome[];
  user?: CaseUser | null;
  transaction?: CaseTransaction | null;
  screening?: {
    risk_level?: string;
    provider_risk?: string | null;
    reason?: string | null;
    screened_at?: string;
    recognised?: boolean;
  } | null;
  activity?: CaseActivity[];
};

const buildQuery = (params: Record<string, string | number | undefined | null>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qs.set(key, String(value));
  });
  const query = qs.toString();
  return query ? `?${query}` : '';
};

export const formatCaseUserName = (
  user?: Pick<CaseUser, 'name' | 'email'> | null,
) => {
  const first = user?.name?.first?.trim() || '';
  const last = user?.name?.last?.trim() || '';
  return `${first} ${last}`.trim() || user?.email || '—';
};

export const formatCaseReasonLabel = (reason?: string, detail?: string | null) => {
  if (detail?.trim()) return detail.trim();
  if (!reason) return '—';
  return reason
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/(^|\s)\w/g, (c) => c.toUpperCase());
};

export const formatOpenFor = (openForMs?: number) => {
  if (openForMs == null || !Number.isFinite(openForMs) || openForMs < 0) return '—';
  const hours = Math.floor(openForMs / (1000 * 60 * 60));
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
};

export const formatOutcomeLabel = (outcome?: string) => {
  if (outcome === 'CLEARED') return 'Release funds';
  if (outcome === 'CONFIRMED') return 'Reject and restrict';
  if (outcome === 'ESCALATED') return 'External compliance';
  if (!outcome) return '—';
  return outcome.charAt(0) + outcome.slice(1).toLowerCase();
};

export const fetchComplianceCases = async (params: {
  page?: number;
  limit?: number;
  sort?: 'opened_at' | 'status';
  order?: 'asc' | 'desc';
  status?: CaseStatus;
  reason?: string;
  risk_level?: 'HIGH' | 'PROHIBITED';
}) => {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/compliance/cases${buildQuery(params)}`,
  );
  return {
    cases: (response.data.cases || []) as ComplianceCaseListItem[],
    meta: response.data.meta as ListMeta,
  };
};

export const fetchComplianceCase = async (caseNumber: number | string) => {
  const response = await request.get(`${AUTH_URL}/v1/admin/compliance/cases/${caseNumber}`);
  return response.data as CaseDetailResponse;
};

export const fetchTransactionCompliance = async (transactionId: string) => {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/transactions/${transactionId}/compliance`,
  );
  return response.data as TransactionComplianceDetail;
};

export const resolveComplianceCase = async (
  caseNumber: number | string,
  payload: { outcome: CaseOutcome; notes?: string; restrict_account?: boolean },
) => {
  const response = await request.patch(
    `${AUTH_URL}/v1/admin/compliance/cases/${caseNumber}`,
    payload,
  );
  return response.data;
};

export type RescreenResult = {
  outcome: string;
  reason?: string | null;
  deposit: { id: string; status?: string; credited: boolean };
};

/**
 * Retries Chainalysis screening for a deposit whose screening failed. Only
 * such deposits are eligible (the API answers 400 otherwise). A pass credits
 * the deposit and auto-resolves its SCREENING_FAILED case.
 */
export const rescreenDeposit = async (transactionId: string) => {
  const response = await request.post(`${AUTH_URL}/v1/admin/transactions/${transactionId}/rescreen`, {});
  return response.data as RescreenResult;
};

export const whitelistCaseAddresses = async (
  caseNumber: number | string,
  addresses?: string[],
) => {
  const response = await request.post(
    `${AUTH_URL}/v1/admin/compliance/cases/${caseNumber}/whitelist`,
    addresses?.length ? { addresses } : {},
  );
  return response.data;
};

/* ------------------------- Review queues ------------------------- */

export type ReviewQueueKind = 'deposits' | 'withdrawals';

export type ReviewQueueRiskLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'PROHIBITED';

export type ReviewQueueItem = {
  id: string;
  type?: string;
  status?: string;
  currency_code?: string;
  amount?: string;
  chain?: string | null;
  /** Deposits carry the sender; withdrawals the destination. */
  sender_address?: string | null;
  destination_address?: string | null;
  screening?: {
    risk_level?: ReviewQueueRiskLevel | string;
    provider_risk?: string | null;
    reason?: string | null;
    screened_at?: string;
  } | null;
  user?: {
    id?: string;
    name?: { first?: string; last?: string } | null;
    email?: string | null;
    bxlend_id?: string | null;
    status?: string | null;
    kyc_status?: string | null;
  } | null;
  compliance_case?: { id?: string; case_number?: number; status?: string } | null;
  requested_at?: string;
  held_since?: string;
  /** Computed server-side on each request, so it is always current. */
  held_for_ms?: number;
};

/**
 * Transactions that screening has frozen and that are waiting on a decision.
 * This is the real hold queue — distinct from browsing all deposits or
 * withdrawals, which the transactions list does with a `kind` filter.
 */
export const fetchReviewQueue = async (
  kind: ReviewQueueKind,
  params: {
    page?: number;
    limit?: number;
    order?: 'asc' | 'desc';
    risk_level?: string;
    /** The API accepts only these two search fields. */
    field?: 'id' | 'address';
    value?: string;
  },
) => {
  const { field, value, ...rest } = params;
  const response = await request.get(
    `${AUTH_URL}/v1/admin/compliance/review-queue/${kind}${buildQuery({
      ...rest,
      ...(field && value ? { field, value } : {}),
    })}`,
  );
  return {
    items: (response.data.items || []) as ReviewQueueItem[],
    meta: response.data.meta as { page: number; limit: number; page_count: number; total_count: number },
  };
};
